import { useNavigate } from 'react-router-dom';
import Button from '../../resuable-ui/button';
import styles from './index.module.scss';
import { formatAmount } from '../../../utils/funcs';
import MarkIconSvg from '../../svgs/MarkIconSvg';
import { useCompleteCart } from '../../../integrations/react_query/mutations';
import { Tracking } from '../../../analytics/tracking';
import { EVENTS } from '../../../utils/events/index';
import { captureException } from '../../../integrations/sentry/capture';

const CompleteTransaction = ({
	data,
	totalAmount,
	totalPayment,
	handlePrintReceipt,
	isPrinting,
}: {
	data: { [key: string]: any };
	totalAmount: number;
	totalPayment: number;
	handlePrintReceipt: () => Promise<void>;
	isPrinting: boolean;
}) => {
	const { mutateAsync } = useCompleteCart();

	const navigate = useNavigate();

	const handleNextSales = async () => {
		try {
			await mutateAsync('');
			Tracking.capture(EVENTS.SALES.NEXT_SALE_CLICKED, {
				transactionId: data?.transactionId,
				totalAmount,
				totalPayment,
				changeDue: +totalPayment - +totalAmount,
			});

			navigate('/terminal');
		} catch (err: any) {
			captureException(err, {
				fn: 'CompleteTransaction.handleNextSales',
				args: { transactionId: data?.transactionId },
			});
			Tracking.capture(EVENTS.SALES.NEXT_SALE_FAILED, {
				transactionId: data?.transactionId,
				error: err?.message || 'Unknown error',
			});
		}
	};

	return (
		<>
			{/* Mobile View */}
			<div
				className="md:hidden h-[calc(100vh_-_120px)] bg-white flex flex-col justify-between w-full"
				style={{ marginTop: 'var(--navbar-height)' }}
			>
				<div className="px-0 py-5 space-y-4 w-full">
					<div className="flex justify-between items-center border-b border-gray-200 w-full px-6">
						<span className="text-gray-700 text-sm font-medium">Paid</span>
						<span className="text-gray-900 text-2xl font-bold">₦{formatAmount(totalPayment)}</span>
					</div>
					<div className="flex justify-between items-center border-b border-gray-200 w-full px-6">
						<span className="text-gray-700 text-sm font-medium">Change</span>
						<span className="text-gray-900 text-2xl font-bold">₦{formatAmount(+totalPayment - +totalAmount)}</span>
					</div>
				</div>

				<div className="flex-1 flex flex-col justify-center space-y-4 w-full">
					<div className="w-full flex justify-center">
						<MarkIconSvg />
					</div>

					<div className="w-full text-center">
						<h2 className="text-2xl font-bold text-gray-900">Transaction Completed</h2>
						<p className="text-sm text-gray-500 mt-1">Transaction ID: {data?.transactionId}</p>
					</div>

					<div className="w-full flex gap-4 px-2">
						<Button
							type="button"
							disabled={isPrinting}
							isBordered={true}
							styles="py-4"
							style={{ width: '50%', fontSize: '1.1em' }}
							onClick={handlePrintReceipt}
						>
							{isPrinting ? 'Printing...' : 'Print Receipt'}
						</Button>

						<Button
							type="button"
							disabled={true}
							isBordered={true}
							styles="py-4"
							style={{ width: '50%', fontSize: '1.1em' }}
						>
							Email Receipt
						</Button>
					</div>
				</div>

				<div className="px-6 pb-4">
					<button
						type="button"
						onClick={handleNextSales}
						className="w-full py-4 px-4 bg-gray-100 rounded-lg flex items-center justify-between hover:bg-gray-200 active:bg-gray-300"
					>
						<span className="text-green-600 font-semibold text-lg">Next Sale</span>
						<span className="text-green-600">→</span>
					</button>
				</div>
			</div>

			{/* Desktop View */}
			<section className={`hidden md:flex d-flex flex-column align-items-stretch w-100 ${styles.wrapper}`}>
				<div className={`d-flex align-items-center justify-content-between w-100`}>
					<Button {...{ styles: `${styles.btn} py-3 py-xl-4 py-xxl-4 px-4` }}>
						<div className="d-flex justify-content-between align-items-center">
							<div>Paid</div>
							<div className={styles.value}>
								<span>&#8358; </span>
								{formatAmount(totalPayment)}
							</div>
						</div>
					</Button>

					<Button {...{ styles: `${styles.btn} py-3 py-xl-4 p-xxl-4 px-4` }}>
						<div className="d-flex justify-content-between align-items-center">
							<div>Change</div>
							<div className={styles.value}>
								<span>&#8358; </span>
								{formatAmount(+totalPayment - +totalAmount)}
							</div>
						</div>
					</Button>
				</div>

				<div className="text-center my-5">
					<div style={{ textAlign: 'center', display: 'flex', justifySelf: 'center' }}>
						<MarkIconSvg />
					</div>

					<div className={styles.tCompleted}>
						<span>Transaction Completed</span>
					</div>
					<div className={styles.tID}>
						<span>Transaction ID: {data?.transactionId}</span>
					</div>
				</div>

				<div className="text-center mt-5">
					<Button
						type="button"
						disabled={isPrinting}
						isBordered={true}
						styles="py-4 mx-3"
						style={{ width: '35%', fontSize: '1.1em' }}
						onClick={handlePrintReceipt}
					>
						{isPrinting ? 'Printing...' : 'Print Receipt'}
					</Button>

					<Button
						type="button"
						disabled={true}
						isBordered={true}
						styles="py-4 mx-3"
						style={{ width: '35%', fontSize: '1.1em' }}
					>
						Email Receipt
					</Button>
				</div>

				<div className={`${styles.nextSale} text-center mt-5`} onClick={handleNextSales}>
					Next Sale
				</div>
			</section>
		</>
	);
};

export default CompleteTransaction;
